import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { convertMoneyToGrapeMoney } from 'utils'

import { MedicalDetails } from './MedicalDetails'
import { SmartBudgetPlan } from './Plan'
import PlanCard from './PlanCard/PlanCard'
import { type MedicalPlanDisplayItem } from '../../medicalPlanTypes'
import { medicalPlanToPlanData } from '../../smartBudgetUtils'
import { type CoverageType, type StepComponentProps } from '../../types'

interface SmartBudgetMedicalPlanProps {
  plans: MedicalPlanDisplayItem[],
  selectedPlan: MedicalPlanDisplayItem | null | undefined,
  isLoadingPlans: boolean,
  plansError: GrapeAPIErrorType | null,
  viewingCoverageType: CoverageType | null,
  setViewingCoverageType: (planType: CoverageType | null) => void,
  remainingAllowance: number,
  onSelectPlan: (plan: MedicalPlanDisplayItem | null) => void,
  isIndividual: boolean,
  navigateToSpecificStep: StepComponentProps['navigateToSpecificStep']
}

export function SmartBudgetMedicalPlan ({
  plans,
  selectedPlan,
  isLoadingPlans: isLoading,
  plansError: error,
  viewingCoverageType,
  setViewingCoverageType,
  remainingAllowance,
  onSelectPlan,
  isIndividual,
  navigateToSpecificStep,
}: SmartBudgetMedicalPlanProps) {
  if (isLoading) {
    return (
      <PlanCard.Loading />
    )
  }

  if (error) {
    return (
      <PlanCard.Error coverageType='medical' error={error as GrapeAPIErrorType} />
    )
  }

  if (!selectedPlan) {
    return (
      <PlanCard.Empty>
        <div className='text-gray-500'>No medical plans work for this budget strategy</div>
      </PlanCard.Empty>
    )
  }

  return (
    <SmartBudgetPlan
      allPlans={plans}
      selectedPlan={selectedPlan}
      getPlanKey={(plan) => plan.plan.id}
      planToPlanData={(plan) => medicalPlanToPlanData(plan, isIndividual)}
      planToPlanPremium={(plan) => convertMoneyToGrapeMoney(plan.total_premium)}
      remainingAllowance={remainingAllowance}
      coverageType='medical'
      viewingCoverageType={viewingCoverageType}
      setViewingCoverageType={setViewingCoverageType}
      onSelectPlan={(plan) => onSelectPlan(plan)}
      renderPlanDetails={(plan, handleClosePanel) => <MedicalDetails onLaunchVerificationModal={handleClosePanel} medicalPlan={plan} isIndividual={isIndividual} navigateToSpecificStep={navigateToSpecificStep} />}
      popoverTitle='Similar medical plans for this budget strategy:'
    />
  )
}

SmartBudgetMedicalPlan.displayName = 'SmartBudget.MedicalPlan'
